import { inject, injectable } from 'inversify'
import { action, computed, makeObservable, observable } from 'mobx'
import type { UnsavedWork } from '@core/interfaces/unsaved-work'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import type { RouteTransition } from '@core/routing/route-transition'
import { Types } from '@core/ioc/types'
import type { LocalStorageGateway } from '@core/services/local-storage.gateway'

const STORAGE_KEY = 'infra-demo-draft-note'

/**
 * The demo page's one piece of persisted state: a scratch note that survives
 * reload, there to show the storage gateway doing its job.
 */
@injectable()
export class InfraDemoRepository implements UnsavedWork, UserScopedState {
  draftNotePm = ''

  /**
   * A second note that is deliberately *not* written on every keystroke, so
   * there is something genuine to lose. The persisted draft above can never be
   * dirty, which makes it useless for demonstrating a navigation guard.
   */
  editedNotePm = ''
  savedNotePm = ''

  @inject(Types.ILocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway

  constructor() {
    makeObservable(this, {
      draftNotePm: observable,
      editedNotePm: observable,
      savedNotePm: observable,
      isDirty: computed,
      setEditedNote: action,
      saveNote: action,
      discardUnsavedWork: action,
      resetUserScopedState: action,
    })
  }

  // Called from the InfraDemoPage route's onEnter — the presenter is transient
  // and rebuilt per visit, so restoring from it would re-read on every render.
  restore = action(() => {
    const stored = this.localStorageGateway.getValue(STORAGE_KEY)
    if (typeof stored === 'string') {
      this.draftNotePm = stored
    }
  })

  get isDirty(): boolean {
    return this.editedNotePm !== this.savedNotePm
  }

  setEditedNote(note: string) {
    this.editedNotePm = note
  }

  saveNote() {
    this.savedNotePm = this.editedNotePm
  }

  /**
   * UnsavedWork. Consulted both for `beforeunload` (document exit — every
   * route) and, scoped by `isAffectedBy` below, for an app-initiated
   * navigation away from this page (Router.goToId asks before touching the
   * URL). Leaving any other route in the app, or reload/back/forward, leaves
   * the edit here untouched — the user finds it again on return.
   */
  hasUnsavedWork(): boolean {
    return this.isDirty
  }

  /**
   * Only the transition off this page's own route can lose this draft — an
   * unrelated navigation elsewhere in the app has nothing to do with it.
   */
  isAffectedBy(transition: RouteTransition): boolean {
    return transition.from === 'InfraDemoPage' && transition.to !== 'InfraDemoPage'
  }

  /** UnsavedWork. "Leave and lose them" has to actually lose them. */
  discardUnsavedWork(): void {
    this.editedNotePm = this.savedNotePm
  }

  setDraftNote = action((note: string) => {
    this.draftNotePm = note
    this.localStorageGateway.setValue(STORAGE_KEY, note)
  })

  /**
   * Only the unsaved-work pair, and both fields of it — `editedNotePm` is
   * what the demo input renders, but `savedNotePm` is its dirty-tracking
   * baseline, so leaving it behind would let the next user's first edit start
   * from the previous user's last saved text (the same leak, one field later).
   * `draftNotePm` deliberately stays: it is `localStorage`-backed on purpose,
   * there to demonstrate the storage gateway surviving a reload (see
   * `restore` above), not session-scoped.
   */
  resetUserScopedState(): void {
    this.editedNotePm = ''
    this.savedNotePm = ''
  }
}
